import type { TFunction } from 'i18next';
import type { AudioDeviceContextValue } from 'providers/AudioDeviceProvider';
import { deviceNames } from './deviceNames';

export type AudioMessageTone = 'info' | 'success' | 'warning' | 'error';
export type AudioMessageAction = 'open-settings' | 'switch-to-recommended';

export interface AudioMessage {
  tone: AudioMessageTone;
  title: string;
  description?: string;
  action?: AudioMessageAction;
  /** Disappears on its own; everything else stays until closed or the state changes. */
  transient: boolean;
}

export type AudioMessageSource = Pick<
  AudioDeviceContextValue,
  'state' | 'selectedDevice' | 'recommendedCandidate' | 'recommendedAvailable' | 'settings'
>;

export function messageForState(input: AudioMessageSource, t: TFunction): AudioMessage | null {
  const names = deviceNames(input);
  const persistent = (
    key: string,
    tone: AudioMessageTone,
    action: AudioMessageAction = 'open-settings'
  ): AudioMessage => ({
    tone,
    title: t(`audio.messages.${key}.title`, names),
    description: t(`audio.messages.${key}.description`, names),
    action,
    transient: false,
  });

  switch (input.state) {
    case 'passive':
      return null;
    case 'searching':
      return { tone: 'info', title: t('audio.messages.searching'), transient: false };
    case 'recommendedConnected':
      return { tone: 'success', title: t('audio.messages.connected', names), transient: true };
    case 'confirmedConnected':
      if (input.recommendedAvailable && input.settings.notifyWhenRecommendedAvailable) {
        return persistent('recommendedAvailable', 'info', 'switch-to-recommended');
      }
      return { tone: 'success', title: t('audio.messages.connected', names), transient: true };
    case 'recommendedMissing':
      return persistent('recommendedMissing', 'warning');
    case 'recommendedInaccessible':
      return persistent('recommendedInaccessible', 'warning');
    case 'recommendedNeverSeen':
      return persistent('recommendedNeverSeen', 'warning');
    case 'confirmedMissing':
      return persistent('confirmedMissing', 'warning');
    case 'confirmedInaccessible':
      return persistent('confirmedInaccessible', 'warning');
    case 'accessBlocked':
      return persistent('accessBlocked', 'error');
    case 'noDevice':
      return persistent('noDevice', 'error');
  }
}
